/**
 * Blossom — drawer.js
 * <drawer-component> — side/top/bottom panels built on a native <dialog>
 * (see Theme.Dialog in global.js). Used for the cart, mobile menu, search
 * overlay and collection filters.
 *
 * Markup: snippets/drawer.liquid
 * Open with any element: data-open="DrawerId" aria-controls="DrawerId"
 * Events: theme:drawer:open / theme:drawer:close  { id }
 */
(() => {
  if (!window.Theme || !Theme.Dialog || customElements.get('drawer-component')) return;

  class DrawerComponent extends Theme.Dialog {
    static openEvent = Theme.events.DRAWER_OPEN;
    static closeEvent = Theme.events.DRAWER_CLOSE;

    connectedCallback() {
      super.connectedCallback();
      if (!this.dialog || this.swipeBound) return;
      this.swipeBound = true;
      this.panel = this.dialog.querySelector('[data-panel]');
      this.bindSwipe();
      this.bindEditorEvents();
    }

    /**
     * Swipe to close on touch devices. Side drawers follow the finger (the
     * overlay fades with them) and either close or spring back on release;
     * bottom sheets close on a downward swipe from the top of their content.
     * Swipes that start inside a horizontal scroller (category bubbles,
     * sliders) are left to the scroller.
     */
    bindSwipe() {
      if (!this.panel) return;
      const position = this.dialog.dataset.position || 'right';
      const overlay = this.dialog.querySelector('[data-overlay]');
      const sideDrawer = position === 'right' || position === 'left' || position === 'full';
      const closingSign = position === 'right' ? 1 : -1;
      let startX = 0;
      let startY = 0;
      let startTime = 0;
      let axis = null;
      let offset = 0;
      let tracking = false;

      const release = () => {
        this.panel.style.transition = '';
        this.panel.style.transform = '';
        if (overlay) {
          overlay.style.transition = '';
          overlay.style.opacity = '';
        }
      };

      this.panel.addEventListener(
        'touchstart',
        (event) => {
          const touch = event.touches[0];
          startX = touch.clientX;
          startY = touch.clientY;
          startTime = event.timeStamp;
          axis = null;
          offset = 0;
          tracking = !event.target.closest('.menu-drawer__bubbles, .slider, .scroll-x, [data-swipe-ignore]');
        },
        { passive: true }
      );

      this.panel.addEventListener(
        'touchmove',
        (event) => {
          if (!tracking || !sideDrawer) return;
          const touch = event.touches[0];
          const dx = touch.clientX - startX;
          const dy = touch.clientY - startY;
          if (!axis) {
            if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
            axis = Math.abs(dx) > Math.abs(dy) * 1.2 ? 'x' : 'y';
          }
          if (axis !== 'x') return;
          offset = closingSign > 0 ? Math.max(dx, 0) : Math.min(dx, 0);
          this.panel.style.transition = 'none';
          this.panel.style.transform = `translateX(${offset}px)`;
          if (overlay) {
            overlay.style.transition = 'none';
            overlay.style.opacity = String(1 - Math.min(Math.abs(offset) / this.panel.offsetWidth, 1));
          }
        },
        { passive: true }
      );

      this.panel.addEventListener(
        'touchend',
        (event) => {
          if (!tracking) return;
          tracking = false;
          const touch = event.changedTouches[0];
          const dx = touch.clientX - startX;
          const dy = touch.clientY - startY;
          if (sideDrawer) {
            if (axis !== 'x') return;
            const velocity = Math.abs(offset) / Math.max(event.timeStamp - startTime, 1);
            const shouldClose = Math.abs(offset) > this.panel.offsetWidth * 0.3 || (velocity > 0.45 && Math.abs(offset) > 24);
            // Hand the transform back to CSS: it springs back, or slides out from where the finger left it.
            release();
            if (shouldClose) this.close();
            return;
          }
          const vertical = Math.abs(dy) > Math.abs(dx) * 1.5;
          if (position === 'bottom' && vertical && dy > 90 && this.panel.scrollTop <= 0) this.close();
        },
        { passive: true }
      );

      this.panel.addEventListener(
        'touchcancel',
        () => {
          tracking = false;
          release();
        },
        { passive: true }
      );
    }

    /** Theme editor: open when a block/section inside the drawer is selected. */
    bindEditorEvents() {
      if (!Theme.designMode) return;
      document.addEventListener('shopify:section:select', (event) => {
        if (this.contains(event.target) || this.dataset.sectionId === event.detail.sectionId) this.open();
      });
      document.addEventListener('shopify:section:deselect', (event) => {
        if (this.contains(event.target) || this.dataset.sectionId === event.detail.sectionId) this.close();
      });
    }
  }

  customElements.define('drawer-component', DrawerComponent);
})();
